import React, { useContext } from 'react';
import { useEffect, useState } from 'react';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { ProductImage, ProductTrackingType } from '../../../clients/quipli-service/product/product';
import {
  CustomerProduct,
  ProductCustomService,
  ProductOption,
} from '../../../clients/customer-api/product/customer-product';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { ProductPricingTable } from '../../product-pricing-table/product-pricing-table';
import { AddOnProducts } from '../addon-products';
import { Dictionary, keyBy, partition } from 'lodash';
import { Location } from '../../../clients/quipli-service/location/location';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { ProductImageGallery } from '../image-gallery/image-gallery';
import './product.scss';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { ProductFormV2 } from '../../product-form/product-form-v2';
import { Customizations, OptionSelection } from '../customizations';
import { Label } from '../../../clients/quipli-service/label/label';
import { durationToHuman } from '../../../util/date-time-util';
import { RelatedProducts } from '../related-products/related-products';
import { SelectForm } from '../../../components/select-form/select-form';
import { TenantContext } from '../../../context/tenant-context';
import { UrlAliasType } from '../../../clients/customer-api/url-alias/url-alias';
import { Button } from '../../../components/form/button';
import { DateTime } from 'luxon';
import { useTagDiscount } from '../../hooks/use-tag-discount';

export type EcommerceProduct = CustomerProduct & {
  services: ProductCustomService[];
  options: ProductOption[];
  relatedProducts: CustomerProduct[];
  addOns: CustomerProduct[];
  images: ProductImage[];
};

export type EcommerceUser = {
  isLoggedIn: boolean;
  userId: number;
  sessionId: string;
};

type AddonSelection = Pick<
  CustomerProduct,
  | 'id'
  | 'name'
  | 'allowSale'
  | 'allowRent'
  | 'maxQuantityAvailable'
  | 'minimumOrderQuantity'
  | 'recommendedQuantity'
  | 'quantityIncrement'
  | 'stock'
  | 'saleStock'
  | 'trackingType'
> & {
  quantity: number;
  selected: boolean;
  isAvailable: boolean;
  type: OrderType;
};

export type ProductAddonSelection = {
  [productId: number]: AddonSelection;
};

export const ViewProductV2 = ({
  productId,
  locationId,
  user,
}: {
  productId: number;
  locationId: number;
  user: EcommerceUser;
}) => {
  const {
    getProductById,
    getProductCustomizations,
    getRelatedProducts,
    getProductAddOns,
    getProductImages,
    getMultipleLabels,
    listLocations,
    getConfigurations,
    getUrlAliases,
    getMultipleRentalAvailability,
  } = useCustomerAPI();

  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<(Location & { urlAlias: string })[]>([]);
  const [location, setLocation] = useState<Location & { urlAlias: string }>();
  const [product, setProduct] = useState<EcommerceProduct>();
  const [customerProduct, setCustomerProduct] = useState<CustomerProduct>();
  const [labels, setLabels] = useState<Dictionary<Label>>();
  const [orderType, setOrderType] = useState(OrderType.Rental);
  const [selectedServices, setSelectedServices] = useState<{
    [serviceId: number]: ProductCustomService & {
      quantity?: number;
      selectedOption?: { label: string; value: string };
    };
  }>({});
  const [selectedOptions, setSelectedOptions] = useState<{
    [optionId: number]: { option: ProductOption; selection?: OptionSelection };
  }>({});
  const [selectedAddOns, setSelectedAddOns] = useState<ProductAddonSelection>({});
  const [configs, setConfigs] = useState<Dictionary<Configuration>>();
  const [rentalDates, setRentalDates] = useState<{ start?: DateTime; end?: DateTime }>({});

  const { contextPath } = useContext(TenantContext);
  const contextPathUrl = contextPath && !contextPath.includes('legacy-app') ? contextPath : '/';

  const { tagDiscount } = useTagDiscount(customerProduct);

  const init = async () => {
    const [loc, labels, configs, p, customizations, related, adds, productImages] = await Promise.all([
      listLocations(),
      getMultipleLabels([
        'L_NA',
        'L_Home',
        'L_Minimum_Rental_Duration',
        'L_Price',
        'L_BUILD_PRODUCT',
        'L_RENT_NOW',
        'L_Rental_Start_Date',
        'L_Rental_End_Date',
        'M_Add_to_Cart',
      ]),
      getConfigurations([
        'CONF_USER_UNAPPROVED_HIDE_PRICES',
        'CONF_SHOW_ADD_CART_BUTTON',
        'CONF_SHOW_RENTAL_END_TIME',
        'CONF_REALTIME_CALENDAR',
        'CONF_HIDE_CUSTOM_BUILDER_PRICES',
        'CONF_SHOW_UNITS_AVAILABLE',
        'CONF_ENABLE_AFFIRM',
        'CONF_LOCATION_NOT_REQUIRED',
        'CONF_SHOW_PRICE_FOR_RENTAL_DURATION',
        'CONF_MAX_ITEMS_PER_ORDER',
        'CONF_CURRENCY',
        'CONF_ECOMMERCE_SALE_REQUIRE_IN_STOCK',
        'CONF_SHOW_PRICES_WITH_TAX',
        'CONF_PRICE_TAX_LABEL',
      ]),
      getProductById(productId),
      getProductCustomizations(productId),
      getRelatedProducts(productId),
      getProductAddOns(productId, locationId),
      getProductImages(productId),
    ]);
    setLabels(keyBy(labels, 'key'));
    const keyedConfigs = keyBy(configs, 'variable');
    setConfigs(keyedConfigs);

    const aliases = await getUrlAliases(
      UrlAliasType.Shop,
      loc.map((l) => l.id),
    );
    setLocations(loc.map((l) => ({ ...l, urlAlias: aliases.find((a) => a.entityId == l.id)?.alias ?? '' })));
    if (locationId) {
      setLocation({
        ...(loc.find((l) => l.id == locationId) as Location),
        urlAlias: aliases.find((a) => a.entityId == locationId)?.alias ?? '',
      });
    }

    setCustomerProduct(p);

    // Set product stuff
    setProduct({
      ...p,
      services: customizations.services,
      options: customizations.options,
      relatedProducts: related,
      addOns: adds,
      images: productImages,
    });

    // Set current order type
    const currentType = p.allowRent ? OrderType.Rental : OrderType.Sale;

    setSelectedAddOns(
      adds.reduce((acc: any, a: CustomerProduct) => {
        // const addonAvailability = addonAvailabilities.find((av) => av.id == a.id)!;
        const stock = a.stock?.find((s) => s.locationId == locationId);
        const type = orderType === 'Rental' && a.allowRent ? OrderType.Rental : OrderType.Sale;
        const availableNumber =
          type === OrderType.Rental
            ? stock?.rentalStock ?? a.maxQuantityAvailable ?? 0
            : stock?.saleStock ?? a.saleStock;

        const available = a.trackingType === ProductTrackingType.NonInventory || availableNumber > 0;

        return {
          ...acc,
          [a.id]: {
            id: a.id,
            name: a.name,
            allowSale: a.allowSale,
            allowRent: a.allowRent,
            maxQuantityAvailable: a.maxQuantityAvailable,
            minimumOrderQuantity: a.minimumOrderQuantity,
            recommendedQuantity: a.recommendedQuantity,
            quantityIncrement: a.quantityIncrement,
            stock: a.stock,
            saleStock: a.saleStock,
            type,
            quantity: available
              ? a.trackingType === ProductTrackingType.NonInventory
                ? a.minimumOrderQuantity ?? 1
                : (a.recommendedQuantity ?? 1) > availableNumber
                ? availableNumber
                : a.recommendedQuantity
              : 0,
            selected: available && p.preselectAddOns === true,
            isAvailable: available,
            trackingType: a.trackingType,
          },
        } as ProductAddonSelection;
      }, {}),
    );

    setOrderType(currentType);

    setLoading(false);
  };

  const getAddonQuantitiesBasedOnAvailability = async (
    addons: Omit<AddonSelection, 'selected' | 'isAvailable'>[],
    preSelect: boolean,
  ): Promise<Pick<AddonSelection, 'id' | 'isAvailable' | 'selected'>[]> => {
    const [rentalArray, saleArray] = partition(
      addons.filter((a) => a.quantity > 0),
      (a) => a.type === OrderType.Rental,
    );

    // Get availability for all rental addons
    const rentalAvailability = rentalArray.length
      ? await getMultipleRentalAvailability(
          rentalArray.map(({ id, quantity }) => ({ productId: id, quantity })),
          locationId,
        )
      : [];
    const saleAvailability = saleArray.map((a) => {
      const stock = a.stock?.find((s) => s.locationId == locationId);
      return {
        productId: a.id,
        availability: (stock?.saleStock ?? a.saleStock) > 0,
      };
    });

    return addons.map((a) => {
      const addonSchedule = rentalAvailability.find((av) => av.productId == a.id);
      let available = false;

      if (a.trackingType === ProductTrackingType.NonInventory) {
        available = true;
      } else {
        if (!location?.checkAvailability || product?.trackingType === ProductTrackingType.NonInventory) {
          available = true;
        } else if (a.type === OrderType.Rental) {
          available =
            addonSchedule?.availability.length && rentalDates.start && rentalDates.end
              ? addonSchedule.availability.some((a) => {
                  return a.start! <= rentalDates.start! && (a.end ? a.end >= rentalDates.end! : true);
                })
              : false;
        } else {
          available = saleAvailability.find((av) => av.productId == a.id)?.availability ?? false;
        }
      }

      return {
        id: a.id,
        selected: available && preSelect,
        isAvailable: available,
      };
    });
  };

  const refreshAddons = async (addonSelections: ProductAddonSelection, getAvailability: boolean = true) => {
    if (!product) return;

    const addonAvailabilities = getAvailability
      ? await getAddonQuantitiesBasedOnAvailability(Object.values(addonSelections), product.preselectAddOns === true)
      : [];

    setSelectedAddOns(
      Object.keys(addonSelections).reduce((acc: any, k: string) => {
        const addon = addonSelections[parseInt(k)];
        const addonAvailability = addonAvailabilities.find((av) => av.id == parseInt(k));
        return {
          ...acc,
          [k]: {
            ...addon,
            isAvailable: getAvailability ? addonAvailability?.isAvailable || false : true,
          },
        };
      }, {}),
    );
  };

  const reset = () => {
    setLoading(true);
    setLocations([]);
    setLocation(undefined);
    setProduct(undefined);
    setLabels(undefined);
    setOrderType(OrderType.Rental);
    setSelectedServices({});
    setSelectedAddOns({});
    setConfigs(undefined);
  };

  useEffect(() => {
    init();

    return reset();
  }, [productId, locationId, user]);

  useEffect(() => {
    if (location && product && rentalDates.start && rentalDates.end) {
      refreshAddons(selectedAddOns);
    }
  }, [orderType, rentalDates]);

  const getYoutubeId = (youtubeVideo: string) => {
    if (youtubeVideo.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/)) {
      return youtubeVideo.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/)?.[1];
    } else return youtubeVideo?.split('?')[0].split('/').pop();
  };

  return !loading && product && configs && labels ? (
    <div>
      <div className="view-product-v2-container container mb-8">
        <div className="row">
          <div className="col-12">
            <h1 className="text-[1.5rem] font-bold">
              {product.name}
              {orderType === OrderType.Rental ? ' Rental' : ''}
            </h1>
          </div>

          <div className="col-md-7 col-lg-9">
            <div className="row">
              <div className="col-lg-6 gallery-col">
                <ProductImageGallery images={product.images} />
              </div>

              <div className="col-lg-6">
                {location && (
                  <>
                    {product.allowRent && (
                      <>
                        {!(configs['CONF_USER_UNAPPROVED_HIDE_PRICES']?.value === '1' && !user.isLoggedIn) &&
                          !product.hidePrices && (
                            <ProductPricingTable
                              productId={product.id}
                              locationId={location.id}
                              userId={user.userId}
                              customerProduct={customerProduct}
                            />
                          )}
                        <Typography type="text-sm">
                          {labels['L_Minimum_Rental_Duration']?.caption ?? 'Minimum Rental Duration'}:{' '}
                          <b>{durationToHuman(product.minRentalDuration)}</b>
                        </Typography>
                        {product.maxRentalDuration && (
                          <Typography type="text-sm">
                            Maximum Rental Duration: <b>{durationToHuman(product.maxRentalDuration)}</b>
                          </Typography>
                        )}
                      </>
                    )}

                    <Customizations
                      productId={product.id}
                      isRental={orderType === OrderType.Rental}
                      location={location}
                      selectedServices={selectedServices}
                      selectedOptions={selectedOptions}
                      hideServicePrices={configs['CONF_HIDE_CUSTOM_BUILDER_PRICES']?.value === '1'}
                      setSelectedServices={setSelectedServices}
                      setSelectedOptions={setSelectedOptions}
                    />

                    {Object.keys(selectedAddOns).length > 0 && (
                      <AddOnProducts
                        addOns={product.addOns}
                        location={location}
                        user={user}
                        orderType={orderType}
                        selectedAddOns={selectedAddOns}
                        refreshAddons={refreshAddons}
                        hidePricesNotLoggedIn={
                          !user.isLoggedIn && configs['CONF_USER_UNAPPROVED_HIDE_PRICES']?.value === '1'
                        }
                        taxInclusive={configs['CONF_SHOW_PRICES_WITH_TAX']?.value === '1'}
                        taxLabel={configs['CONF_PRICE_TAX_LABEL']?.value ?? 'Tax'}
                      />
                    )}
                  </>
                )}

                <div className="description mt-8">
                  <h4 className="mb-4">
                    <b>Description</b>
                  </h4>
                  <div
                    className="product-description text-sm"
                    dangerouslySetInnerHTML={{ __html: product.description || '' }}
                  ></div>
                  {product.youtubeVideo && (
                    <div className=" mt-4">
                      <h5 className="mx-0">
                        <b>{product.name}</b>
                      </h5>
                      <div className=" w-full relative pb-[60%]">
                        <iframe
                          className="w-full h-full top-0 left-0 absolute"
                          width="560"
                          height="315"
                          src={`https://www.youtube.com/embed/${getYoutubeId(product.youtubeVideo)}`}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen={true}
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5 col-lg-3">
            {location && product.locationIds.indexOf(location.id) >= 0 ? (
              <ProductFormV2
                product={product}
                location={location}
                orderType={orderType}
                addons={selectedAddOns}
                services={selectedServices}
                options={selectedOptions}
                hidePrices={!user.isLoggedIn && configs['CONF_USER_UNAPPROVED_HIDE_PRICES']?.value === '1'}
                user={user}
                configs={configs}
                labels={labels}
                setOrderType={(type: OrderType) => setOrderType(type)}
                tagDiscount={tagDiscount}
                rentalDates={rentalDates}
                setRentalDates={setRentalDates}
              />
            ) : (
              <>
                <div id="select-location" className="p-2">
                  <Typography type="text-md">
                    This product is available at the following locations:
                    <span id="location-required" className="text-[var(--error-800)] hidden">
                      *
                    </span>
                  </Typography>
                  <SelectForm
                    value={null}
                    onChange={(v) => {
                      if (v) {
                        const newLocation = locations.find((l) => l.id == v.value);
                        if (configs['CONF_LOCATION_NOT_REQUIRED']?.value === '1') {
                          window.location.href = `${contextPathUrl}${newLocation?.urlAlias}/${product.urlKeyword}`;
                        } else {
                          setLocation(newLocation);
                        }
                      }
                    }}
                    options={product.locationIds.map((id) => ({
                      value: id,
                      label: locations.find((l) => l.id == id)?.name ?? 'N/A',
                    }))}
                  ></SelectForm>
                </div>
                {configs['CONF_SHOW_ADD_CART_BUTTON']?.value === '1' && (
                  <Button
                    className={`button border-[var(--primary-color-500)] bg-[var(--primary-color-500)] text-white w-full mt-3.5`}
                    onClick={() => {
                      $('#select-location').addClass('p-3 rounded-md border-1 border-solid border-[var(--error-800)]');
                      $('#location-required').removeClass('hidden');
                    }}
                  >
                    {labels['M_Add_to_Cart']?.caption ?? 'Add to Cart'}
                  </Button>
                )}

                <Button
                  className={`button border-[var(--secondary-color-500)] bg-[var(--secondary-color-500)] text-white w-full mt-2.5`}
                  onClick={() => {
                    $('#select-location').addClass('border-1 border-solid border-red');
                    $('#location-required').removeClass('hidden');
                  }}
                >
                  {orderType === OrderType.Rental
                    ? labels['L_RENT_NOW']?.caption ?? 'Rent Now'
                    : labels['L_BUILD_PRODUCT']?.caption ?? 'Buy Now'}
                </Button>
              </>
            )}
          </div>

          <div className="description-bottom mt-4">
            <h4 className="mb-4">
              <b>Description</b>
            </h4>
            <div className="product-description" dangerouslySetInnerHTML={{ __html: product.description || '' }}></div>
            {product.youtubeVideo && (
              <div className="mt-4">
                <h5 className="mx-0">
                  <b>{product.name}</b>
                </h5>
                <div className="relative w-full pb-[60%]">
                  <iframe
                    className="w-full h-full top-0 left-0 absolute"
                    width="560"
                    height="315"
                    src={`https://www.youtube.com/embed/${getYoutubeId(product.youtubeVideo)}`}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen={true}
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {location && (
        <RelatedProducts
          relatedProducts={product.relatedProducts}
          location={location}
          user={user}
          labels={labels}
          configs={configs}
        />
      )}
    </div>
  ) : (
    <Spinner />
  );
};
